import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  ItBacklogItem,
  PRIORITY_CONFIG,
  CATEGORY_CONFIG,
  STATUS_CONFIG,
} from '@/types/itBacklog';
import { useUpdateBacklogItem, useDeleteBacklogItem } from '@/hooks/useItBacklog';
import { 
  Clock, 
  Calendar, 
  User, 
  Link2, 
  Trash2, 
  Edit2,
  AlertTriangle,
  CheckCircle,
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useState } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

interface BacklogItemDetailProps {
  item: ItBacklogItem | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onEdit: () => void;
}

export function BacklogItemDetail({ item, open, onOpenChange, onEdit }: BacklogItemDetailProps) {
  const [actualHours, setActualHours] = useState<string>(item?.actual_hours?.toString() || '');
  const [blockers, setBlockers] = useState<string>(item?.blockers || '');
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  
  const updateItem = useUpdateBacklogItem();
  const deleteItem = useDeleteBacklogItem();
  
  if (!item) return null;
  
  const priority = PRIORITY_CONFIG[item.priority];
  const category = CATEGORY_CONFIG[item.category];
  const status = STATUS_CONFIG[item.status];
  
  const requesterName = item.requester?.display_name || 
    (item.requester?.first_name && item.requester?.last_name 
      ? `${item.requester.first_name} ${item.requester.last_name}` 
      : 'Não definido');
  
  const assigneeName = item.assignee?.display_name || 
    (item.assignee?.first_name && item.assignee?.last_name 
      ? `${item.assignee.first_name} ${item.assignee.last_name}` 
      : 'Não atribuído');
  
  const handleSaveActualHours = () => {
    if (actualHours !== (item.actual_hours?.toString() || '')) {
      updateItem.mutate({
        id: item.id,
        data: { actual_hours: parseFloat(actualHours) || 0 },
      });
    }
  };
  
  const handleSaveBlockers = () => {
    if (blockers !== (item.blockers || '')) {
      updateItem.mutate({
        id: item.id,
        data: { blockers: blockers || null },
      });
    }
  };
  
  const handleClearBlockers = () => {
    setBlockers('');
    updateItem.mutate({
      id: item.id,
      data: { blockers: null },
    });
  };
  
  const handleDelete = async () => {
    await deleteItem.mutateAsync(item.id);
    setShowDeleteDialog(false);
    onOpenChange(false);
  };
  
  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
          <SheetHeader className="space-y-4">
            <div className="flex items-start justify-between">
              <div className="space-y-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge className={status.color}>
                    {status.label}
                  </Badge>
                  <Badge variant="outline" className={priority.color}>
                    {priority.icon} {priority.label}
                  </Badge>
                  <Badge variant="secondary">
                    {category.icon} {category.label}
                  </Badge>
                </div>
                <SheetTitle className="text-left text-xl">
                  {item.title}
                </SheetTitle>
              </div>
            </div>
            
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={onEdit}>
                <Edit2 className="h-4 w-4 mr-1" />
                Editar
              </Button>
              <Button 
                variant="destructive" 
                size="sm" 
                onClick={() => setShowDeleteDialog(true)}
              >
                <Trash2 className="h-4 w-4 mr-1" />
                Excluir
              </Button>
            </div>
          </SheetHeader>
          
          <div className="mt-6 space-y-6">
            {/* Description */}
            {item.description && (
              <div>
                <h4 className="text-sm font-medium mb-2">Descrição</h4>
                <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                  {item.description}
                </p>
              </div>
            )}
            
            {/* Acceptance Criteria */}
            {item.acceptance_criteria && (
              <div>
                <h4 className="text-sm font-medium mb-2 flex items-center gap-1">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  Critérios de Aceite
                </h4>
                <p className="text-sm text-muted-foreground whitespace-pre-wrap bg-muted/50 p-3 rounded-lg">
                  {item.acceptance_criteria}
                </p>
              </div>
            )}
            
            <Separator />
            
            {/* Info Grid */}
            <div className="grid grid-cols-2 gap-4 text-sm">
              {item.module && (
                <div>
                  <span className="text-muted-foreground">Módulo</span>
                  <p className="font-medium">{item.module}</p>
                </div>
              )}
              
              {item.estimated_hours && (
                <div>
                  <span className="text-muted-foreground flex items-center gap-1">
                    <Clock className="h-3 w-3" /> Estimado
                  </span>
                  <p className="font-medium">{item.estimated_hours}h</p>
                </div>
              )}
              
              {item.due_date && (
                <div>
                  <span className="text-muted-foreground flex items-center gap-1">
                    <Calendar className="h-3 w-3" /> Data Limite
                  </span>
                  <p className="font-medium">
                    {format(parseISO(item.due_date), "dd/MM/yyyy", { locale: ptBR })}
                  </p>
                </div>
              )}
              
              {item.planned_week && (
                <div>
                  <span className="text-muted-foreground">Semana Planejada</span>
                  <p className="font-medium">
                    {format(parseISO(item.planned_week), "dd/MM/yyyy", { locale: ptBR })}
                  </p>
                </div>
              )}
              
              <div>
                <span className="text-muted-foreground flex items-center gap-1">
                  <User className="h-3 w-3" /> Solicitante
                </span>
                <p className="font-medium">{requesterName}</p>
              </div>
              
              <div>
                <span className="text-muted-foreground flex items-center gap-1">
                  <User className="h-3 w-3" /> Responsável
                </span>
                <p className="font-medium">{assigneeName}</p>
              </div>
              
              {item.started_at && (
                <div>
                  <span className="text-muted-foreground">Iniciado em</span>
                  <p className="font-medium">
                    {format(parseISO(item.started_at), "dd/MM/yyyy HH:mm", { locale: ptBR })}
                  </p>
                </div>
              )}
              
              {item.completed_at && (
                <div>
                  <span className="text-muted-foreground">Concluído em</span>
                  <p className="font-medium">
                    {format(parseISO(item.completed_at), "dd/MM/yyyy HH:mm", { locale: ptBR })}
                  </p>
                </div>
              )}
            </div>
            
            {/* Bug Report Link */}
            {item.bug_report && (
              <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                <span className="text-sm text-muted-foreground flex items-center gap-1 mb-1">
                  <Link2 className="h-3 w-3" /> Bug Report Vinculado
                </span>
                <p className="text-sm font-medium">{item.bug_report.title}</p>
              </div>
            )}
            
            <Separator />
            
            {/* Actual Hours */}
            <div className="space-y-2">
              <Label>Horas Reais</Label>
              <div className="flex gap-2">
                <Input
                  type="number"
                  min="0"
                  step="0.5"
                  value={actualHours}
                  onChange={(e) => setActualHours(e.target.value)}
                  placeholder="Horas gastas"
                  className="w-32"
                />
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={handleSaveActualHours}
                  disabled={updateItem.isPending}
                >
                  Salvar
                </Button>
              </div>
            </div>
            
            {/* Blockers */}
            <div className="space-y-2">
              <Label className="flex items-center gap-1">
                <AlertTriangle className="h-4 w-4 text-orange-500" />
                Bloqueios
              </Label>
              <Textarea
                value={blockers}
                onChange={(e) => setBlockers(e.target.value)}
                placeholder="Descreva o que está impedindo o progresso..."
                className="min-h-[80px]"
              />
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={handleSaveBlockers}
                  disabled={updateItem.isPending}
                >
                  Salvar
                </Button>
                {item.blockers && (
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={handleClearBlockers}
                    disabled={updateItem.isPending}
                    className="text-green-600 hover:text-green-700 hover:bg-green-50"
                  >
                    🔓 Desbloquear
                  </Button>
                )}
              </div>
            </div>
            
            {/* Notes */}
            {item.notes && (
              <div>
                <h4 className="text-sm font-medium mb-2">Notas</h4>
                <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                  {item.notes}
                </p>
              </div>
            )}
            
            {/* Timestamps */}
            <div className="text-xs text-muted-foreground pt-4 border-t">
              <p>Criado em: {format(parseISO(item.created_at), "dd/MM/yyyy HH:mm", { locale: ptBR })}</p>
              <p>Atualizado em: {format(parseISO(item.updated_at), "dd/MM/yyyy HH:mm", { locale: ptBR })}</p>
            </div>
          </div>
        </SheetContent>
      </Sheet>
      
      {/* Delete Confirmation */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir tarefa?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação não pode ser desfeita. A tarefa "{item.title}" será permanentemente removida.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground">
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
